import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { 
  Zap, BookOpen, Calendar, Flag, Car, ClipboardList, Users, Trophy, 
  ToggleLeft, Shield, ShieldCheck, Award, Info, Euro
} from 'lucide-react';

const HelpTab = () => {
  return (
    <div className="space-y-6">
      {/* Quick Start Guide */}
      <Card className="border-2 border-blue-400">
        <CardHeader className="bg-blue-500/10 dark:bg-blue-500/20">
          <CardTitle className="flex items-center text-blue-700 dark:text-blue-400">
            <Zap className="mr-2" size={24} />
            🚀 Snelstart Gids - Pool Instellen
          </CardTitle>
          <CardDescription>Volg deze stappen om je F1 pool klaar te maken voor het seizoen</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <ol className="space-y-4 list-decimal list-inside">
            <li className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <strong className="text-blue-800 dark:text-blue-200">Seizoen Aanmaken</strong>
              <p className="text-muted-foreground ml-6 mt-1">Ga naar de "Seizoenen" tab en maak een nieuw seizoen aan (bijv. 2026). Het systeem haalt automatisch de race kalender op van de F1 API.</p>
            </li>
            <li className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <strong className="text-blue-800 dark:text-blue-200">Financiële Instellingen</strong>
              <p className="text-muted-foreground ml-6 mt-1">Stel de kosten per race, administratiekosten en betaallink in. Dit bepaalt de prijzenpot en wordt getoond in de welkomst email.</p>
            </li>
            <li className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <strong className="text-blue-800 dark:text-blue-200">Voorspelling Toggles</strong>
              <p className="text-muted-foreground ml-6 mt-1">Bepaal in de "Instellingen" tab welke voorspellingen je wilt activeren (Sprint, P2/P3, etc.).</p>
            </li>
            <li className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <strong className="text-blue-800 dark:text-blue-200">Seizoen Starten</strong>
              <p className="text-muted-foreground ml-6 mt-1">Klik op "Start Seizoen" om het seizoen te activeren. Gebruikers kunnen nu voorspellingen indienen.</p>
            </li>
            <li className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <strong className="text-blue-800 dark:text-blue-200">Race Resultaten Invoeren</strong>
              <p className="text-muted-foreground ml-6 mt-1">Na elke race, ga naar "Resultaten" en voer de uitslag in. Klik op "Uitslag Opslaan" om punten te berekenen.</p>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Tabs Explanation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BookOpen className="mr-2 text-[#FDB913]" size={24} />
            📋 Tab Overzicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-border rounded-lg bg-secondary/30">
              <h4 className="font-bold flex items-center mb-2">
                <Calendar className="mr-2 text-[#FDB913]" size={18} />
                Seizoenen
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Nieuw seizoen aanmaken</li>
                <li>• Seizoen starten/stoppen</li>
                <li>• Financiële instellingen (kosten, betaallink)</li>
                <li>• Actieve seizoenen beheren</li>
              </ul>
            </div>
            
            <div className="p-4 border border-border rounded-lg bg-secondary/30">
              <h4 className="font-bold flex items-center mb-2">
                <Flag className="mr-2 text-[#FDB913]" size={18} />
                Races
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Race kalender bekijken</li>
                <li>• Races toevoegen/bewerken/verwijderen</li>
                <li>• Sprint weekenden markeren</li>
                <li>• Datums en tijden aanpassen</li>
              </ul>
            </div>
            
            <div className="p-4 border border-border rounded-lg bg-secondary/30">
              <h4 className="font-bold flex items-center mb-2">
                <Car className="mr-2 text-[#FDB913]" size={18} />
                Coureurs
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Coureurs en teams beheren</li>
                <li>• Nieuwe coureurs toevoegen</li>
                <li>• Team wisselingen bijwerken</li>
                <li>• Test coureurs voor resultaten</li>
              </ul>
            </div>
            
            <div className="p-4 border border-border rounded-lg bg-secondary/30">
              <h4 className="font-bold flex items-center mb-2">
                <ClipboardList className="mr-2 text-[#FDB913]" size={18} />
                Voorspellingen
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Alle voorspellingen per race bekijken</li>
                <li>• <strong>Kampioenschap voorspellingen per user bekijken/invullen</strong></li>
                <li>• Punten breakdown per gebruiker</li>
                <li>• Voorspellingen exporteren naar Excel</li>
              </ul>
            </div>
            
            <div className="p-4 border border-border rounded-lg bg-secondary/30">
              <h4 className="font-bold flex items-center mb-2">
                <Users className="mr-2 text-[#FDB913]" size={18} />
                Gebruikers
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Gebruikerslijst bekijken (alfabetisch)</li>
                <li>• Admin/Super Admin rechten toekennen</li>
                <li>• Gebruikers activeren/deactiveren</li>
                <li>• <strong>Betaalstatus markeren</strong></li>
                <li>• Wachtwoord resetten</li>
                <li>• Gebruikers exporteren naar CSV (incl. betaalstatus)</li>
                <li>• Accounts verwijderen</li>
              </ul>
            </div>
            
            <div className="p-4 border border-border rounded-lg bg-secondary/30">
              <h4 className="font-bold flex items-center mb-2">
                <Trophy className="mr-2 text-[#FDB913]" size={18} />
                Resultaten
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Race uitslagen invoeren</li>
                <li>• Sprint resultaten (bij sprint weekenden)</li>
                <li>• Punten automatisch berekenen</li>
                <li>• Opnieuw scoren indien nodig</li>
              </ul>
            </div>
            
            <div className="p-4 border border-border rounded-lg bg-secondary/30">
              <h4 className="font-bold flex items-center mb-2">
                <ToggleLeft className="mr-2 text-[#FDB913]" size={18} />
                Instellingen
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Voorspelling types aan/uit zetten</li>
                <li>• Sprint opties configureren</li>
                <li>• Kwalificatie P2/P3 toggle</li>
                <li>• Per seizoen configureerbaar</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scoring System */}
      <Card className="border-2 border-[#FDB913]">
        <CardHeader className="bg-[#FDB913]/10 dark:bg-[#FDB913]/20">
          <CardTitle className="flex items-center text-foreground">
            <Award className="mr-2 text-[#FDB913]" size={24} />
            📊 Puntensysteem Overzicht
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-bold mb-2">Race Top 5 Punten (per coureur):</h5>
              <table className="text-sm w-full">
                <tbody>
                  <tr className="border-b"><td className="py-1">Juiste positie:</td><td className="text-right font-bold">25 pts</td></tr>
                  <tr className="border-b"><td className="py-1">1 positie verschil:</td><td className="text-right font-bold">18 pts</td></tr>
                  <tr className="border-b"><td className="py-1">2 posities verschil:</td><td className="text-right font-bold">15 pts</td></tr>
                  <tr className="border-b"><td className="py-1">3 posities verschil:</td><td className="text-right font-bold">12 pts</td></tr>
                  <tr className="border-b"><td className="py-1">4 posities verschil:</td><td className="text-right font-bold">10 pts</td></tr>
                  <tr className="border-b"><td className="py-1">5 posities verschil:</td><td className="text-right font-bold">8 pts</td></tr>
                  <tr className="border-b"><td className="py-1">6 posities verschil:</td><td className="text-right font-bold">6 pts</td></tr>
                  <tr className="border-b"><td className="py-1">7 posities verschil:</td><td className="text-right font-bold">4 pts</td></tr>
                  <tr className="border-b"><td className="py-1">8 posities verschil:</td><td className="text-right font-bold">2 pts</td></tr>
                  <tr><td className="py-1">9 posities verschil:</td><td className="text-right font-bold">1 pt</td></tr>
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground mt-2">* Coureur moet in top 10 eindigen om punten te scoren</p>
            </div>
            <div>
              <h5 className="font-bold mb-2">Bonus Punten:</h5>
              <table className="text-sm w-full">
                <tbody>
                  <tr className="border-b"><td className="py-1">Snelste ronde:</td><td className="text-right font-bold">10 pts</td></tr>
                  <tr className="border-b"><td className="py-1">Safety Car correct:</td><td className="text-right font-bold">10 pts</td></tr>
                  <tr className="border-b"><td className="py-1">VSC correct:</td><td className="text-right font-bold">10 pts</td></tr>
                  <tr className="border-b"><td className="py-1">DNF count correct:</td><td className="text-right font-bold">10 pts</td></tr>
                  <tr><td className="py-1">Regen race correct:</td><td className="text-right font-bold">10 pts</td></tr>
                </tbody>
              </table>

              <h5 className="font-bold mb-2 mt-6">Kampioenschap (Top 3):</h5>
              <table className="text-sm w-full">
                <tbody>
                  <tr className="border-b"><td className="py-1">Juiste positie:</td><td className="text-right font-bold">50 pts</td></tr>
                  <tr className="border-b"><td className="py-1">1 positie verschil:</td><td className="text-right font-bold">40 pts</td></tr>
                  <tr className="border-b"><td className="py-1">2 posities verschil:</td><td className="text-right font-bold">30 pts</td></tr>
                  <tr><td className="py-1">Coureur/team niet in top 5:</td><td className="text-right font-bold">0 pts</td></tr>
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground mt-2">* Geldt voor zowel Wereldkampioen als Constructeurskampioen</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Roles Explanation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="mr-2 text-[#FDB913]" size={24} />
            👥 Gebruikersrollen Uitleg
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border-2 border-border rounded-lg">
              <h5 className="font-bold flex items-center text-muted-foreground">
                <Users className="mr-2" size={18} />
                Gebruiker
              </h5>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Voorspellingen indienen</li>
                <li>• Eigen voorspellingen bekijken</li>
                <li>• Ranglijst bekijken</li>
                <li>• Profiel beheren</li>
              </ul>
            </div>
            <div className="p-4 border-2 border-blue-300 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/30">
              <h5 className="font-bold flex items-center text-blue-700 dark:text-blue-400">
                <Shield className="mr-2" size={18} />
                Admin
              </h5>
              <ul className="text-sm text-blue-600 dark:text-blue-400 mt-2 space-y-1">
                <li>• Alles wat gebruiker kan</li>
                <li>• Resultaten invoeren</li>
                <li>• Races/coureurs beheren</li>
                <li>• Instellingen aanpassen</li>
                <li>• <strong>Verschijnt WEL in de stand</strong></li>
              </ul>
            </div>
            <div className="p-4 border-2 border-purple-300 dark:border-purple-700 rounded-lg bg-purple-50 dark:bg-purple-900/30">
              <h5 className="font-bold flex items-center text-purple-700 dark:text-purple-400">
                <ShieldCheck className="mr-2" size={18} />
                Super Admin
              </h5>
              <ul className="text-sm text-purple-600 dark:text-purple-400 mt-2 space-y-1">
                <li>• Alle admin rechten</li>
                <li>• Gebruikers promoveren/degraderen</li>
                <li>• Seizoenen aanmaken/verwijderen</li>
                <li>• <strong>Verschijnt NIET in de stand</strong></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQ / Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Info className="mr-2 text-[#FDB913]" size={24} />
            ❓ Veelgestelde Vragen & Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-3 bg-secondary rounded-lg">
              <h5 className="font-bold">Hoe markeer ik een race als Sprint Weekend?</h5>
              <p className="text-sm text-muted-foreground mt-1">Ga naar de "Races" tab, klik op "Bewerken" bij de race en zet "Sprint Weekend" aan. Vul ook de sprint kwalificatie en sprint race tijden in.</p>
            </div>
            <div className="p-3 bg-secondary rounded-lg">
              <h5 className="font-bold">Wat als ik verkeerde resultaten heb ingevoerd?</h5>
              <p className="text-sm text-muted-foreground mt-1">Pas de resultaten aan en klik op "Opnieuw Scoren". De punten worden dan herberekend voor alle deelnemers.</p>
            </div>
            <div className="p-3 bg-secondary rounded-lg">
              <h5 className="font-bold">Hoe exporteer ik de gebruikerslijst?</h5>
              <p className="text-sm text-muted-foreground mt-1">Ga naar de "Gebruikers" tab en klik op "Exporteer CSV". Je krijgt een bestand met alle usernames, emails en betaalstatus.</p>
            </div>
            <div className="p-3 bg-secondary rounded-lg">
              <h5 className="font-bold">Krijg ik een melding bij nieuwe registraties?</h5>
              <p className="text-sm text-muted-foreground mt-1">Ja! Alle admins ontvangen een email wanneer een nieuwe gebruiker zich registreert. Controleer of de gebruiker heeft betaald voordat je het account activeert.</p>
            </div>
            <div className="p-3 bg-secondary rounded-lg">
              <h5 className="font-bold">Hoe markeer ik een gebruiker als betaald?</h5>
              <p className="text-sm text-muted-foreground mt-1">Ga naar de "Gebruikers" tab en klik op "💰 Markeer betaald" bij de betreffende gebruiker. De status wordt direct bijgewerkt.</p>
            </div>
            <div className="p-3 bg-secondary rounded-lg">
              <h5 className="font-bold">Kan ik kampioenschap voorspellingen voor gebruikers invullen?</h5>
              <p className="text-sm text-muted-foreground mt-1">Ja! Ga naar de "Voorspellingen" tab, selecteer een gebruiker en vul de Wereldkampioen/Constructeurskampioen voorspellingen in.</p>
            </div>
            <div className="p-3 bg-secondary rounded-lg">
              <h5 className="font-bold">Waarom verschijnt een Super Admin niet in de stand?</h5>
              <p className="text-sm text-muted-foreground mt-1">Dit is bewust ontworpen zodat de pool-beheerder kan meedoen voor de lol zonder de competitie te beïnvloeden.</p>
            </div>
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border border-yellow-300 dark:border-yellow-700">
              <h5 className="font-bold text-yellow-800 dark:text-yellow-400">💡 Pro Tips:</h5>
              <ul className="text-sm text-yellow-700 dark:text-yellow-500 mt-1 space-y-1">
                <li>• Voer resultaten zo snel mogelijk in na de race - gebruikers zijn benieuwd!</li>
                <li>• Controleer de pole tijd formaat: MM:SS.mmm (bijv. 01:28.456)</li>
                <li>• Bij twijfel over een DNF, check de officiële FIA uitslag</li>
                <li>• Maak regelmatig een backup van je gebruikerslijst via CSV export</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpTab;
